import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { IconButton } from '@livecommerce/design-system';
import { useDrawerContext } from 'contexts/DrawerContext.jsx';
import useClickOutside from 'hooks/useClickOutside.js';
import { productsNavigationTransitionConfig } from 'constants/animationConfig.js';
import {
  StyledDrawer,
  StyledLabels,
  DrawerTopbar,
  ActionButtons,
  DrawerContent,
  FirstLevelDrawer,
  AnimatedSecondLevelDrawer,
  DrawerFooter,
} from './styles.js';

const DRAWER_WIDTH = 480;

const Drawer = () => {
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_WIDTH);
  const {
    closeDrawer,
    closeDrawerInState,
    drawerIsOpen,
    removeDrawer,
    drawerPages,
    page,
    setPage,
    isCloseOnOverlayActive,
    drawerContext,
    scrollDrawerContent,
  } = useDrawerContext();
  const isFormDirty = drawerContext?.form?.formState?.isDirty;
  const drawerContent = drawerPages?.[page]?.content;
  const { ref: drawerRef } = useClickOutside((e) => {
    if (e.target.closest('[id^=multi-select]')) return;
    if (e.target.closest('[id^=navigation-topbar]')) return;
    if (e.target.closest('[id^=downshift]')) return;
    if (e.target.closest('[role="dialog"]')) return;
    if (e.target.closest('[role="alert"]')) return;
    if (e.target.closest('#alert')) return;
    if (!drawerIsOpen) return;
    if (!isCloseOnOverlayActive) return;
    if (isFormDirty) {
      drawerContext?.unsavedChangesModal?.open();
      return;
    }
    if (page === 0) closeDrawerInState();
    drawerPages?.[page]?.onClose?.();
  });

  useEffect(() => {
    if (!drawerContent) return;
    if (!drawerRef.current) return;
    setDrawerWidth(drawerRef.current.offsetWidth);
  }, [drawerContent, drawerRef]);
  useEffect(() => {
    if (!closeDrawer) return;

    const onKeyUp = (event) => {
      if (event.key !== 'Escape') return;
      closeDrawer();
    };
    document.addEventListener('keyup', onKeyUp);
    return () => {
      document.removeEventListener('keyup', onKeyUp);
    };
  }, [closeDrawer]);

  if (!drawerIsOpen && !drawerContent) return;

  const closeSubsequentDrawerPage = () => {
    if (isFormDirty) {
      drawerContext?.unsavedChangesModal?.open();
      return;
    }
    setPage((prevPage) => prevPage - 1);
  };

  return (
    <StyledDrawer
      ref={drawerRef}
      id="product-navigation-drawer-container"
      initial={{
        x: drawerWidth,
      }}
      animate={{
        x: drawerIsOpen ? 0 : drawerWidth,
      }}
      onAnimationComplete={({ x }) => {
        if (!x || drawerIsOpen) return;
        removeDrawer();
      }}
      transition={productsNavigationTransitionConfig}
    >
      {drawerPages.length > 0 && (
        <FirstLevelDrawer ref={drawerRef} fadedOut={page !== 0} scrollDrawerContent={scrollDrawerContent}>
          {(drawerPages[0].title || drawerPages[0].subtitle || drawerPages[0].headerContent) && (
            <DrawerTopbar>
              {(drawerPages[0].title || drawerPages[0].subtitle) && !drawerPages[0].headerContent && (
                <StyledLabels
                  label={drawerPages[0].title}
                  description={drawerPages[0].subtitle}
                  showFullDescription
                  descriptionVariation="roman"
                />
              )}
              {drawerPages[0].headerContent && drawerPages[0].headerContent}
              <ActionButtons>
                <IconButton
                  tabIndex={drawerIsOpen ? 0 : -1}
                  variant="transparent"
                  type="button"
                  iconName="cancel"
                  onClick={() => closeDrawer()}
                  data-test-id="closeDrawerButton"
                />
              </ActionButtons>
            </DrawerTopbar>
          )}
          <DrawerContent scrollDrawerContent={scrollDrawerContent}>{drawerPages[0].content}</DrawerContent>
          {drawerPages[0].footer && <DrawerFooter>{drawerPages[0].footer}</DrawerFooter>}
        </FirstLevelDrawer>
      )}
      <AnimatePresence>
        {page !== 0 && (
          <AnimatedSecondLevelDrawer
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={productsNavigationTransitionConfig}
          >
            {(drawerPages[0].title || drawerPages[0].subtitle || drawerPages[0].headerContent) && (
              <DrawerTopbar>
                {(drawerPages[page].title || drawerPages[page].subtitle) && !drawerPages[page]?.headerContent && (
                  <StyledLabels
                    label={drawerPages[page].title}
                    description={drawerPages[page].subtitle}
                    showFullDescription
                    descriptionVariation="roman"
                  />
                )}
                {drawerPages[page]?.headerContent && <div>{drawerPages[page].headerContent}</div>}
                <ActionButtons>
                  <IconButton
                    tabIndex={drawerIsOpen ? 0 : -1}
                    variant="transparent"
                    type="button"
                    iconName="cancel"
                    onClick={closeSubsequentDrawerPage}
                    data-test-id="closeDrawerButton"
                  />
                </ActionButtons>
              </DrawerTopbar>
            )}
            <DrawerContent>{drawerPages[page].content}</DrawerContent>
            {drawerPages[page]?.footer && <DrawerFooter>{drawerPages[page].footer}</DrawerFooter>}
          </AnimatedSecondLevelDrawer>
        )}
      </AnimatePresence>
    </StyledDrawer>
  );
};

export default Drawer;
